import React, { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { submitContact } from "@/lib/api";
import { Mail, MapPin, Clock } from "lucide-react";
import { PawIcon } from "@/components/Icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    location: "",
    service_type: "tagesbetreuung",
    dog_count: "",
    dog_size: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const isCat = form.service_type === "katzenbetreuung";

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email,
        location: form.location,
        service_type: form.service_type,
        message: form.message,
      };
      if (!isCat) {
        if (form.dog_count) payload.dog_count = parseInt(form.dog_count, 10);
        if (form.dog_size) payload.dog_size = form.dog_size;
      }
      await submitContact(payload);
      setSent(true);
      toast.success(t.contact.success);
      setForm({
        name: "",
        phone: "",
        email: "",
        location: "",
        service_type: "tagesbetreuung",
        dog_count: "",
        dog_size: "",
        message: "",
      });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  const items = [
    { icon: Mail, label: "info@carol-tierbetreuung.ch", href: "mailto:info@carol-tierbetreuung.ch", testid: "contact-email" },
    { icon: MapPin, label: t.contact.city, testid: "contact-city" },
    { icon: Clock, label: t.contact.hours, testid: "contact-hours" },
  ];

  return (
    <section id="contact" className="section-blush py-20 sm:py-28 relative overflow-hidden" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 relative">
        <Reveal className="lg:col-span-5">
          <span className="font-script text-3xl text-[#f4956a]">{t.contact.info} 🐾</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 mb-4">{t.contact.title}</h2>
          <p className="text-[#6b3f2a]/85 text-sm sm:text-base mb-8 leading-relaxed">{t.contact.subtitle}</p>

          <ul className="space-y-3">
            {items.map((it, i) => {
              const Icon = it.icon;
              const Wrapper = it.href ? "a" : "div";
              return (
                <li key={i}>
                  <Wrapper
                    href={it.href}
                    className="flex items-center gap-3 bg-white rounded-2xl px-4 py-3 shadow-sm hover:shadow-md transition-shadow group"
                    data-testid={it.testid}
                  >
                    <span className="w-10 h-10 rounded-xl bg-[#fdf3ec] flex items-center justify-center text-[#f4956a] group-hover:bg-[#f4956a] group-hover:text-white transition-colors">
                      <Icon size={18} />
                    </span>
                    <span className="text-[#6b3f2a] font-semibold text-sm sm:text-base">{it.label}</span>
                  </Wrapper>
                </li>
              );
            })}
          </ul>
        </Reveal>

        <Reveal delay={150} className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-white space-y-4"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-[#6b3f2a] mb-1.5">
                  {t.contact.name} <span className="text-[#f4956a]">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder={t.contact.placeholderName}
                  className="cute-input"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#6b3f2a] mb-1.5">
                  {t.contact.phone} <span className="text-[#f4956a]">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={onChange("phone")}
                  placeholder={t.contact.placeholderPhone}
                  className="cute-input"
                  data-testid="contact-input-phone"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-[#6b3f2a] mb-1.5">
                  {t.contact.email} <span className="text-[#f4956a]">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder={t.contact.placeholderEmail}
                  className="cute-input"
                  data-testid="contact-input-email"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#6b3f2a] mb-1.5">
                  {t.contact.location} <span className="text-[#f4956a]">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.location}
                  onChange={onChange("location")}
                  placeholder={t.contact.placeholderLocation}
                  className="cute-input"
                  data-testid="contact-input-location"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#6b3f2a] mb-1.5">
                {t.contact.service} <span className="text-[#f4956a]">*</span>
              </label>
              <Select
                value={form.service_type}
                onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}
              >
                <SelectTrigger className="cute-input h-auto py-[14px]" data-testid="contact-input-service">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="tagesbetreuung" data-testid="service-tag">🏡 {t.contact.tagesbetreuung}</SelectItem>
                  <SelectItem value="spaziergang" data-testid="service-spa">🐕 {t.contact.spaziergang}</SelectItem>
                  <SelectItem value="katzenbetreuung" data-testid="service-kat">🐱 {t.contact.katzenbetreuung}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!isCat && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="dog-fields">
                <div>
                  <label className="block text-sm font-bold text-[#6b3f2a] mb-1.5">
                    {t.contact.dogCount}
                  </label>
                  <Select
                    value={form.dog_count}
                    onValueChange={(v) => setForm((f) => ({ ...f, dog_count: v }))}
                  >
                    <SelectTrigger className="cute-input h-auto py-[14px]" data-testid="contact-input-dogcount">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <SelectItem key={n} value={String(n)} data-testid={`dogcount-${n}`}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#6b3f2a] mb-2">
                    {t.contact.dogSize}
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    {[
                      { v: "klein", label: t.contact.sizeSmall },
                      { v: "gross", label: t.contact.sizeLarge },
                    ].map((opt) => (
                      <label
                        key={opt.v}
                        className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-2xl border-2 cursor-pointer transition-all ${
                          form.dog_size === opt.v
                            ? "border-[#f4956a] bg-[#fdf3ec] text-[#6b3f2a]"
                            : "border-[#fbe1de] bg-white text-[#6b3f2a]/80 hover:bg-[#fdf3ec]"
                        }`}
                        data-testid={`dogsize-${opt.v}-label`}
                      >
                        <input
                          type="radio"
                          name="dog_size"
                          value={opt.v}
                          checked={form.dog_size === opt.v}
                          onChange={onChange("dog_size")}
                          className="accent-[#f4956a]"
                          data-testid={`dogsize-${opt.v}`}
                        />
                        <span className="text-sm font-semibold">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-bold text-[#6b3f2a] mb-1.5">
                {t.contact.message} <span className="text-[#f4956a]">*</span>
              </label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={onChange("message")}
                placeholder={t.contact.placeholderMessage}
                className="cute-input"
                data-testid="contact-input-message"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full justify-center"
              data-testid="contact-submit"
            >
              <PawIcon size={16} />
              {submitting ? "…" : t.contact.submit}
            </button>

            {sent && (
              <p className="text-center text-[#6b3f2a] font-semibold pt-2" data-testid="contact-success">
                {t.contact.success}
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
